import {
  dayMonthDateString,
  TimeOffEvent,
  TimeOffRequestsEvent,
  timeStringFrom24HourTimeString,
  User,
} from '@nextforce/api'
import { Icon, makeStyles, Text, useTheme } from '@rneui/themed'
import { onlineManager } from '@tanstack/react-query'
import { t } from 'app/i18l'
import React, { useState } from 'react'
import { View } from 'react-native'
import { Card } from 'shared/components/card'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { StackedUserAvatars } from 'shared/components/user/stacked_user_avatars'
import { UserAvatar } from 'shared/components/user/user_avatar'
import { EventDescriptionModal } from '../../../../shared/components/event_description_modal'
import { ReadMoreView } from '../../../../shared/components/read_more'

interface Props {
  event: TimeOffRequestsEvent
  expanded: boolean
  showSummaryTiles: boolean
  onPressTimeOffCard(): void
  onEventExpand(event: TimeOffRequestsEvent): void
}

const STORE_TIME_OFF_MARGIN = 3
const TIME_OFF_SUMMARY_AVATARS_MAX_COUNT = 5

export function StoreTimeOffEventView({
  event,
  onPressTimeOffCard,
  onEventExpand,
  expanded,
  showSummaryTiles,
}: Props) {
  const styles = useStyles()
  const { theme } = useTheme()
  const showSummary = showSummaryTiles && event.timeOffEvents.length > 1

  const approvedTimeOffEvents = event.timeOffEvents.filter(
    timeOffEvent => timeOffEvent.status === 'APPROVED'
  )

  if (!approvedTimeOffEvents.length) {
    return <></>
  }
  return (
    <View style={{ flex: 1 }}>
      {showSummary && <StoreTimeOffEventSummaryView />}
      {expanded ? (
        <View style={styles.summaryTimeOffEventsContainer}>
          <View style={styles.expandedTimeOffEventsLeftBorder}></View>
          <View style={styles.expandedTimeOffEventsContainer}>
            {getStoreTimeOffEventViews(approvedTimeOffEvents)}
          </View>
        </View>
      ) : (
        (!showSummary || !showSummaryTiles) &&
        getStoreTimeOffEventViews(approvedTimeOffEvents)
      )}
    </View>
  )

  function getStoreTimeOffEventViews(timeOffEvents: TimeOffEvent[]) {
    return timeOffEvents.map((timeOffEvent, index) => (
      <StoreTimeOffEventCard key={String(index)} timeOffEvent={timeOffEvent} />
    ))
  }

  function StoreTimeOffEventCard({
    timeOffEvent,
  }: {
    timeOffEvent: TimeOffEvent
  }) {
    const [modalVisible, setModalVisible] = useState(false)

    return (
      <PressableOpacity
        onPress={onPressTimeOffCard}
        style={styles.card}
        disabled={!onlineManager.isOnline()}
      >
        <View
          style={{
            paddingVertical: 10,
          }}
        >
          <UserAvatar
            user={timeOffEvent.user}
            size="medium"
            showAvatarIfNoPhone
          />
        </View>
        <View style={{ flex: 1, paddingHorizontal: 10 }}>
          <Text
            style={[styles.name, styles.text]}
          >{`${timeOffEvent.user.firstName} ${timeOffEvent.user.lastName}`}</Text>
          {timeOffEvent.startDate === timeOffEvent.endDate ? (
            <Text style={styles.text}>{`${dayMonthDateString(
              timeOffEvent.startDate
            )}`}</Text>
          ) : (
            <Text style={styles.text}>{`${dayMonthDateString(
              timeOffEvent.startDate
            )} - ${dayMonthDateString(timeOffEvent.endDate)}`}</Text>
          )}
          {timeOffEvent.allDay ? (
            <Text style={styles.text}>{t('allDay')}</Text>
          ) : (
            <Text style={styles.text}>{`${timeStringFrom24HourTimeString(
              timeOffEvent.startTime
            )} - ${timeStringFrom24HourTimeString(
              timeOffEvent.endTime
            )}`}</Text>
          )}
          <ReadMoreView
            textStyle={styles.reasonText}
            onPressReadMore={setModalVisible}
            description={timeOffEvent.reason}
          />
          <EventDescriptionModal
            visible={modalVisible}
            setModalVisible={setModalVisible}
            description={timeOffEvent.reason}
            title={t('reason')}
          />
          <Text style={styles.text}>
            {t('approvedBy')}
            <Text style={styles.name}>
              {` ${timeOffEvent.approver.firstName} ${timeOffEvent.approver.lastName}`}
            </Text>
          </Text>
        </View>
      </PressableOpacity>
    )
  }

  function StoreTimeOffEventSummaryView() {
    return (
      <Card style={styles.summaryCard} alert={true} rightBorder={true}>
        <PressableOpacity
          onPress={() => onEventExpand(event)}
          style={{
            flex: 1,
            flexDirection: 'row',
            margin: 5,
          }}
        >
          <View style={{ flex: 1 }}>
            <Text
              style={{
                fontWeight: 'bold',
                marginBottom: 5,
                marginHorizontal: 10,
              }}
            >
              {t('approvedTimeOff')}
            </Text>
            <AvatarRow
              users={approvedTimeOffEvents.map(
                timeOffEvent => timeOffEvent.user
              )}
              avatarSize={styles.avatarIcon.height}
              avatarCount={TIME_OFF_SUMMARY_AVATARS_MAX_COUNT}
            />
          </View>
          <View style={styles.caretContainer}>
            <View style={styles.caret}>
              <Icon
                name={expanded ? 'angle-down' : 'angle-right'}
                type="font-awesome-5"
                solid
                color={theme.colors?.grey2}
                size={20}
              />
            </View>
          </View>
        </PressableOpacity>
      </Card>
    )
  }
}

export function AvatarRow({
  users,
  avatarSize,
  avatarCount,
  centered = false,
}: {
  users: User[]
  avatarSize: number
  avatarCount: number
  centered?: boolean
}) {
  const styles = useStyles(centered)

  return (
    <View style={styles.avatarRow}>
      {users.map((user, index) => (
        <View style={styles.avatar} key={String(index)}>
          {index === avatarCount ? (
            <StackedUserAvatars user={user} size={avatarSize} />
          ) : index > avatarCount ? (
            <></>
          ) : (
            <UserAvatar user={user} size={40} showAvatarIfNoPhone />
          )}
        </View>
      ))}
    </View>
  )
}

export function getStoreTimeOffEventsHeight(timeOffEvents: TimeOffEvent[]) {
  const approvedTimeOffEventsCount = timeOffEvents.filter(
    timeOffEvent => timeOffEvent.status === 'APPROVED'
  ).length
  return (
    STORE_TIME_OFF_SUMMARY_CARD_HEIGHT +
    STORE_TIME_OFF_MARGIN +
    approvedTimeOffEventsCount *
      (STORE_TIME_OFF_CARD_HEIGHT + STORE_TIME_OFF_MARGIN)
  )
}

export const STORE_TIME_OFF_SUMMARY_CARD_HEIGHT = 100
export const STORE_TIME_OFF_CARD_HEIGHT = 145

const useStyles = makeStyles((theme, centered: boolean) => ({
  summaryCard: {
    height: STORE_TIME_OFF_SUMMARY_CARD_HEIGHT,
    marginTop: STORE_TIME_OFF_MARGIN,
    backgroundColor: theme.colors?.white,
  },
  caretContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  caret: {
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 20,
    height: 21,
    minWidth: 21,
    marginLeft: 5,
  },
  avatarRow: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: centered ? 'center' : 'flex-start',
    alignItems: 'center',
  },
  avatar: {
    margin: 5,
  },
  avatarIcon: {
    height: 20,
  },
  summaryTimeOffEventsContainer: { flexDirection: 'row' },
  expandedTimeOffEventsContainer: {
    flex: 1,
    paddingLeft: 10,
  },
  expandedTimeOffEventsLeftBorder: {
    backgroundColor: theme.colors?.grey7,
    width: 5,
    borderRadius: 10,
    marginVertical: 5,
  },
  card: {
    height: STORE_TIME_OFF_CARD_HEIGHT,
    flexDirection: 'row',
    marginHorizontal: 10,
    marginTop: STORE_TIME_OFF_MARGIN,
    backgroundColor: theme.colors.white,
    borderRadius: 15,
    padding: 10,
    elevation: 3,
  },
  text: {
    padding: 2,
    color: theme.colors.black,
  },
  reasonText: {
    padding: 0,
    marginLeft: 2,
    marginRight: 2,
    marginTop: 1,
    marginBottom: 0,
    color: theme.colors.black,
  },
  name: {
    fontWeight: 'bold',
    fontSize: 16,
  },
}))
